// Client-side downscale: shrink a captured/selected photo to a sane max
// dimension and re-encode as JPEG before upload. Keeps the request small
// (a 1024px JPEG is ~100–400 KB) and strips EXIF. No deps — canvas only.

const MAX_DIM = 1024;
const QUALITY = 0.82;
const MAX_FILE_BYTES = 25 * 1024 * 1024; // pre-downscale guard

export class ImageInputError extends Error {}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      /* fall back to <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Could not read that image."));
      el.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downscaleToDataUrl(file: File): Promise<string> {
  // Only reject a *declared* non-image type. An empty type (common for
  // HEIC from some pickers) is allowed through so Safari/iOS can try its
  // native decoder; loadBitmap fails gracefully if it can't.
  if (file.type && !file.type.startsWith("image/")) {
    throw new ImageInputError(
      "That's not an image. Choose a photo (JPEG, PNG, or WebP).",
    );
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new ImageInputError(
      "That photo is too large (max ~25 MB). Try another.",
    );
  }
  const bmp = await loadBitmap(file);
  const w = "width" in bmp ? bmp.width : 0;
  const h = "height" in bmp ? bmp.height : 0;
  if (!w || !h) throw new Error("Could not read that image.");

  const scale = Math.min(1, MAX_DIM / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));

  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Image processing is unavailable.");
  ctx.drawImage(bmp as CanvasImageSource, 0, 0, cw, ch);
  if ("close" in bmp && typeof bmp.close === "function") bmp.close();

  return canvas.toDataURL("image/jpeg", QUALITY);
}
